import {
  forwardRef,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import { isNil, isString } from 'lodash';
import { IconSize, NoneIcon } from '@ardoq/icons';
import { WithPopoverPlacement, WithPopover } from '@ardoq/popovers';
import {
  DropdownItemColorMap,
  DropdownMenuOptionProps,
  DropdownMenuOptionStatus,
} from '../types';
import { Breadcrumbs2 } from '@ardoq/basic-components';
import {
  ActiveContentWrapper,
  CheckboxWrapper,
  MenuOptionBody,
  MenuOptionInnerWrapper,
  MenuOptionOuterWrapper,
  MenuOptionOuterWrapperWithContent,
  MenuOptionWrapper,
  OptionCheckIcon,
  OptionDescription,
  OptionIcon,
  OptionLabel,
  OptionRightLabel,
} from '../atoms';
import { dropdownMenuOperations } from '../dropdownMenuOperations';
import { CustomOption } from '@ardoq/custom-select-options';
import { colors } from '@ardoq/design-tokens';

const MAX_TEXT_LINES = 2;

const isTruncated = (textNode: HTMLDivElement, maxTextLines: number) => {
  const textHeight = textNode.scrollHeight || 0;
  const textLineHeight = window.getComputedStyle(textNode).lineHeight;
  const lineHeight = parseInt(textLineHeight, 10);

  if (!Number.isInteger(lineHeight)) {
    return false;
  }

  return Math.floor(textHeight / lineHeight) > maxTextLines;
};

export const DropdownMenuOption = forwardRef<
  HTMLDivElement,
  PropsWithChildren<DropdownMenuOptionProps>
>((props, ref) =>
  props.customData ? (
    <DropdownMenuOptionWithContent {...props} ref={ref} />
  ) : (
    <DropdownMenuOptionWithOptions {...props} ref={ref} />
  )
);

const statusColorMaps: Record<DropdownMenuOptionStatus, DropdownItemColorMap> =
  {
    error: {
      default: {
        backgroundColor: colors.surfaceError,
      },
      hover: {
        backgroundColor: colors.red100,
      },
    },
    warning: {
      default: {
        backgroundColor: colors.surfaceWarning,
      },
      hover: {
        backgroundColor: colors.yellow100,
      },
    },
    info: {
      default: {},
      hover: {
        backgroundColor: colors.surfaceInfo,
      },
    },
  };

export const DropdownMenuOptionWithOptions = forwardRef<
  HTMLDivElement,
  PropsWithChildren<DropdownMenuOptionProps>
>((props, ref) => {
  const {
    label,
    rightLabel,
    breadcrumbs,
    leftContent,
    rightContent,
    description,
    isDisabled,
    isFocused,
    isHovered,
    isSelected,
    isAi,
    align = 'justified',
    isChecked,
    children,
    truncateDescription,
    ariaLabel,
    clickId,
    testId,
    intercomTarget,
    isHighlighted,
    keepRightContentActive,
    truncateTitle,
    popoverProps,
    tooltipText,
    buttonType,
    status,
    hideSelectedOptions,
    onClick,
    menuOptionWrapperProps,
  } = props;
  const labelRef = useRef<HTMLDivElement>(null);
  const [isLabelTruncated, setIsLabelTruncated] = useState(false);
  const isStringLabel = isString(label);

  useEffect(() => {
    setIsLabelTruncated(
      Boolean(
        truncateTitle &&
        isStringLabel &&
        labelRef.current &&
        labelRef.current?.scrollHeight &&
        isTruncated(labelRef.current, MAX_TEXT_LINES)
      )
    );
  }, [label, isStringLabel, truncateTitle]);

  const leftIcon = dropdownMenuOperations.getIconProps(props, 'left');
  const rightIcon = dropdownMenuOperations.getIconProps(props, 'right');
  const hasCheckbox = isChecked !== undefined;

  const commonWrapperProps = {
    $isHovered: isHovered,
    $isFocused: isFocused,
    $isClickable: Boolean(onClick),
    $isAi: isAi,
  };

  const {
    onClick: menuOptionWrapperPropsOnClick,
    onMouseMove,
    onMouseOver,
    ...restMenuOptionWrapperProps
  } = menuOptionWrapperProps || {};

  const colorMap = status ? statusColorMaps[status] : undefined;

  const content = (
    <MenuOptionOuterWrapper
      /** See comment in MenuOptionOuterWrapper for why we "duplicate" the props here*/
      {...commonWrapperProps}
      data-tooltip-text={tooltipText}
      $colorMap={colorMap}
      onMouseMove={onMouseMove}
      onMouseOver={onMouseOver}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
        if (menuOptionWrapperPropsOnClick) {
          menuOptionWrapperPropsOnClick(e);
        } else if (onClick && !isDisabled) {
          onClick(e);
        }
      }}
    >
      <MenuOptionWrapper
        role="option"
        aria-selected={hasCheckbox ? isChecked : isSelected || undefined}
        aria-checked={hasCheckbox ? isChecked : undefined}
        aria-disabled={isDisabled ? true : undefined}
        aria-label={ariaLabel}
        ref={ref}
        $hasLeftIcon={!!leftIcon}
        $isDisabled={isDisabled}
        $isSelected={isSelected}
        data-click-id={clickId}
        data-test-id={testId}
        data-intercom-target={intercomTarget || label}
        $isHighlighted={isHighlighted}
        $buttonType={buttonType}
        $colorMap={colorMap}
        {...commonWrapperProps}
        {...restMenuOptionWrapperProps}
      >
        {leftContent}
        <MenuOptionInnerWrapper>
          <MenuOptionBody $align={align} $hasRightIcon={Boolean(rightIcon)}>
            {leftIcon && (
              <OptionIcon
                iconSize={IconSize.MEDIUM}
                iconName={leftIcon.name}
                $isHighlighted={isHighlighted}
                $color={leftIcon.color}
              />
            )}
            <WithPopover
              content={isStringLabel && isLabelTruncated && label}
              preferredPlacement={WithPopoverPlacement.TOP}
            >
              <OptionLabel
                ref={labelRef}
                $shouldTruncate={truncateTitle}
                $isHighlighted={isHighlighted}
                $maxTextLines={MAX_TEXT_LINES}
              >
                {label}
              </OptionLabel>
            </WithPopover>
            {!isNil(rightLabel) && (
              <OptionRightLabel $isDisabled={isDisabled}>
                {rightLabel}
              </OptionRightLabel>
            )}
            {rightIcon && (
              <OptionIcon
                iconName={rightIcon.name}
                $isHighlighted={isHighlighted}
                $color={rightIcon?.color}
                iconSize={IconSize.MEDIUM}
              />
            )}
            {children}
          </MenuOptionBody>
          {breadcrumbs && <Breadcrumbs2 breadcrumbs={breadcrumbs} />}
          {description && (
            <OptionDescription
              $shouldTruncate={truncateDescription}
              $isDisabled={isDisabled}
            >
              {description}
            </OptionDescription>
          )}
        </MenuOptionInnerWrapper>
        {hasCheckbox && !hideSelectedOptions && (
          <CheckboxWrapper>
            {isChecked === true && (
              <OptionCheckIcon iconSize={IconSize.MEDIUM} />
            )}
            {isChecked === false && <NoneIcon iconSize={IconSize.MEDIUM} />}
          </CheckboxWrapper>
        )}
        {!(isDisabled && keepRightContentActive) && rightContent}
      </MenuOptionWrapper>
      {rightContent && isDisabled && keepRightContentActive && (
        <ActiveContentWrapper>{rightContent}</ActiveContentWrapper>
      )}
    </MenuOptionOuterWrapper>
  );

  if (!popoverProps) return content;

  return <WithPopover {...popoverProps}>{content}</WithPopover>;
});

export const DropdownMenuOptionWithContent = forwardRef<
  HTMLDivElement,
  PropsWithChildren<DropdownMenuOptionProps>
>((props, ref) => {
  const {
    label,
    isDisabled,
    isFocused,
    isHovered,
    isSelected,
    isAi,
    ariaLabel,
    clickId,
    testId,
    intercomTarget,
    isHighlighted,
    buttonType,
    onClick,
    menuOptionWrapperProps,
    customData,
    status,
  } = props;

  const colorMap = status ? statusColorMaps[status] : undefined;

  return (
    <MenuOptionOuterWrapperWithContent>
      <MenuOptionWrapper
        role="option"
        aria-disabled={isDisabled ? true : undefined}
        aria-label={ariaLabel}
        ref={ref}
        $hasLeftIcon={false}
        $isDisabled={isDisabled}
        $isFocused={isFocused}
        $isHovered={isHovered}
        $isSelected={isSelected}
        $isAi={isAi}
        $isClickable={Boolean(onClick)}
        data-click-id={clickId}
        data-test-id={testId}
        data-intercom-target={intercomTarget || label}
        $isHighlighted={isHighlighted}
        $buttonType={buttonType}
        $colorMap={colorMap}
        onClick={!isDisabled ? onClick : undefined}
        {...menuOptionWrapperProps}
      >
        <CustomOption customData={customData} />
      </MenuOptionWrapper>
    </MenuOptionOuterWrapperWithContent>
  );
});

DropdownMenuOption.displayName = 'DropdownMenuOption';
